import React, { useEffect, useState, useCallback, useMemo } from 'react';
import NavHeader from '../components/NavHeader';
import {
  fetchCompanyCodes,
  createCompanyCode,
  updateCompanyCode,
  deleteCompanyCode,
} from '../api';

// Management UI for the JE "Co Code" destination entities that appear on the
// VAT package's Tax Invoice Template — which foreign entity the expense is
// passed to, dictated by the client's international team. Mirrors
// PayingBanksAdmin's structure/styling. Reads are open to any authenticated
// user; create/edit/delete are processor/admin-only — enforced server-side,
// so no client-side gating here.
//
// Deleting (or editing) a company code never changes already-generated
// packages: each batch keeps its own snapshot of the selected code's fields.
// The domestic entity 1040 is a fixed constant in the JE and is never
// managed here.

const EMPTY_FORM = {
  code: '',
  entity_name: '',
  description: '',
  active: true,
};

// Nullable string columns: blank inputs are sent as explicit nulls on save.
const NULLABLE_FIELDS = ['description'];

function CompanyCodesAdmin() {
  const [codes, setCodes] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  // Modal state — null = closed, 'add' = new code, UUID = editing that code
  const [modalMode, setModalMode] = useState(null);
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);

  const showToast = useCallback((message, type = 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  }, []);

  const loadCodes = useCallback(async () => {
    try {
      const res = await fetchCompanyCodes();
      const data = res.data;
      setCodes(Array.isArray(data) ? data : []);
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to load company codes');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadCodes();
  }, [loadCodes]);

  const filteredCodes = useMemo(() =>
    codes
      .filter((c) => {
        const q = search.toLowerCase();
        return (
          (c.code || '').toLowerCase().includes(q) ||
          (c.entity_name || '').toLowerCase().includes(q) ||
          (c.description || '').toLowerCase().includes(q)
        );
      })
      .sort((a, b) => (a.code || '').localeCompare(b.code || '')),
    [codes, search]
  );

  const openAdd = () => {
    setFormData({ ...EMPTY_FORM });
    setModalMode('add');
  };

  const openEdit = (code) => {
    setFormData({
      code: code.code || '',
      entity_name: code.entity_name || '',
      description: code.description || '',
      active: code.active !== false,
    });
    setModalMode(code.id);
  };

  const closeModal = () => {
    setModalMode(null);
    setFormData({ ...EMPTY_FORM });
  };

  const setField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const buildPayload = () => {
    const payload = {
      code: formData.code.trim(),
      entity_name: formData.entity_name.trim(),
      active: formData.active,
    };
    for (const field of NULLABLE_FIELDS) {
      const value = (formData[field] || '').trim();
      payload[field] = value || null;
    }
    return payload;
  };

  const handleSave = async () => {
    if (!formData.code.trim() || !formData.entity_name.trim()) return;
    setSaving(true);
    try {
      if (modalMode === 'add') {
        await createCompanyCode(buildPayload());
        showToast('Company code created successfully', 'success');
      } else {
        await updateCompanyCode(modalMode, buildPayload());
        showToast('Company code updated successfully', 'success');
      }
      closeModal();
      loadCodes();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to save company code');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (e, codeId) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this company code? Already-generated packages keep their own copy of the details.')) return;
    try {
      await deleteCompanyCode(codeId);
      showToast('Company code deleted', 'success');
      loadCodes();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to delete company code');
    }
  };

  return (
    <div className="projects-page">
      <NavHeader actions={
        <button className="btn btn-primary" onClick={openAdd}>Add Company Code</button>
      } />

      {/* Toast */}
      {toast && (
        <div className={`toast toast-${toast.type}`}>
          {toast.message}
        </div>
      )}

      {/* Search */}
      <div className="projects-search">
        <input
          type="text"
          placeholder="Search by code, entity, or description..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="search-input"
        />
        {search && (
          <span className="search-count">{filteredCodes.length} of {codes.length} company codes</span>
        )}
      </div>

      {/* Company Codes Table */}
      <div className="projects-table-container">
        {loading ? (
          <div className="loading-spinner">Loading company codes...</div>
        ) : (
          <>
            <table className="projects-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Entity</th>
                  <th>Description</th>
                  <th>Active</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredCodes.map((c) => (
                  <tr
                    key={c.id}
                    className="clickable-row"
                    onClick={() => openEdit(c)}
                  >
                    <td>{c.code}</td>
                    <td>{c.entity_name}</td>
                    <td>{c.description || '—'}</td>
                    <td>{c.active ? 'Yes' : 'No'}</td>
                    <td className="project-actions">
                      <button
                        className="btn btn-reject btn-sm"
                        onClick={(e) => handleDelete(e, c.id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredCodes.length === 0 && search && (
              <div className="projects-empty">No matching company codes.</div>
            )}
            {codes.length === 0 && !search && (
              <div className="projects-empty">No company codes yet. Click "Add Company Code" above.</div>
            )}
          </>
        )}
      </div>

      {/* Modal */}
      {modalMode !== null && (
        <div className="project-modal-overlay" onClick={closeModal}>
          <div className="project-modal" onClick={(e) => e.stopPropagation()}>
            <div className="project-modal-header">
              <h2>{modalMode === 'add' ? 'New Company Code' : 'Edit Company Code'}</h2>
              <button className="project-modal-close" onClick={closeModal}>&times;</button>
            </div>

            <div className="project-modal-body">
              <div className="project-modal-row">
                <label>Code *</label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setField('code', e.target.value)}
                  placeholder="e.g. 1256"
                  autoFocus
                />
              </div>

              <div className="project-modal-row">
                <label>Entity Name *</label>
                <input
                  type="text"
                  value={formData.entity_name}
                  onChange={(e) => setField('entity_name', e.target.value)}
                  placeholder="e.g. Mast Industries UK Limited"
                />
              </div>

              <div className="project-modal-row">
                <label>Description</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setField('description', e.target.value)}
                  placeholder="e.g. Baker McKenzie invoices"
                />
              </div>

              <div className="project-modal-row">
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.active}
                    onChange={(e) => setField('active', e.target.checked)}
                  />
                  Active (shown in the "Company code" dropdown)
                </label>
              </div>
            </div>

            <div className="project-modal-footer">
              <button className="btn btn-secondary" onClick={closeModal}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSave}
                disabled={!formData.code.trim() || !formData.entity_name.trim() || saving}
              >
                {saving ? 'Saving...' : (modalMode === 'add' ? 'Create Company Code' : 'Save Changes')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CompanyCodesAdmin;
